import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
} from '@mui/material';

const CompanyRow = ({ company, projectId }) => {
    const navigate = useNavigate();

    const handleRowClick = () => {
        navigate(`/projects/${projectId}/deficiencies`, {
            state: {
                filterByCompany: {
                    companyId: company.companyId,
                    companyName: company.companyName,
                },
            },
        });
    };

    return (
        <TableRow
            onClick={handleRowClick}
            sx={{
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                    backgroundColor: 'action.hover',
                },
            }}
        >
            <TableCell>{company.companyName}</TableCell>
            <TableCell align="right">{company.openItems}</TableCell>
            <TableCell align="right">{company.completedInPeriod}</TableCell>
            <TableCell align="right">
                {company.onTimeCompletionRate !== null ? `${company.onTimeCompletionRate}%` : 'N/A'}
            </TableCell>
            <TableCell align="right">
                {company.avgCycleTimeDays !== null
                    ? `${company.avgCycleTimeDays.toFixed(1)} days`
                    : 'N/A'}
            </TableCell>
            <TableCell align="right">
                {company.rejectedCount > 0 ? (
                    <Chip
                        label={company.rejectedCount}
                        size="small"
                        color="warning"
                        sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                    />
                ) : (
                    '-'
                )}
            </TableCell>
            <TableCell align="right">
                {company.overdueItems > 0 ? (
                    <Chip
                        label={company.overdueItems}
                        size="small"
                        color="error"
                        sx={{ fontSize: '0.75rem' }}
                    />
                ) : (
                    company.overdueItems
                )}
            </TableCell>
        </TableRow>
    );
};

const CompanyPerformanceView = ({ reportData, projectId }) => {
    if (!reportData) return null;

    const { companyMetrics = [] } = reportData;

    return (
        <Box>
            <Box sx={{ mt: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'left' }}>
                    Company Performance Metrics
                </Typography>
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Company
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Open
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Completed
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    On-Time %
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Avg Cycle Time
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Rejected
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Overdue
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {companyMetrics.map((company) => (
                                <CompanyRow
                                    key={company.companyId}
                                    company={company}
                                    projectId={projectId}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default CompanyPerformanceView;
